/**
 * useCreateArea - Hook for creating areas from template form values
 * useSaveArea - Hook for saving both new and existing areas
 *
 * Converts form values to HangarInsert format and creates/updates the area.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import { createHangar, updateHangar, deleteHangarUnits } from '@/lib/api/hangars'
import { createUnit, updateUnit } from '@/lib/api/units'
import { hangarKeys } from './useHangars'
import { unitKeys } from './useUnits'
import type { HangarInsert, HangarUpdate } from '@/types/hangars'
import type { AreaPlacementFieldsInput } from '@/types/area-placement'
import { AREA_TYPE_PREFIXES } from '@/types/hangars'
import type { SingleUnitFormValues, OutdoorRectangularFormValues } from '@/stores/buildingEditorStore'
import { singleUnitDefaultValues, getSingleUnitTemplate, getSingleRowTemplate, getDoubleRowTemplate, getStandardTHangarTemplate, getNestedTHangarTemplate, getArchHangarTemplate, getOutdoorRectangularTemplate, type SingleRowParams, type DoubleRowParams, type THangarParams, type NestedTHangarParams, type ArchHangarParams } from '@/lib/templates'
import type { PricingFormFields } from '@/lib/schemas/pricing-fields'
import { toast } from 'sonner'

interface CreateAreaParams {
  locationId: string
  formValues: SingleUnitFormValues
  latitude: number
  longitude: number
}

interface SaveAreaParams {
  locationId: string
  formValues: SingleUnitFormValues
  latitude: number
  longitude: number
  // For editing existing areas
  areaId?: string | null
  unitId?: string | null
  // Optional pricing data
  pricing?: PricingFormFields
}

/**
 * Create an area from template form values
 */
export function useCreateArea() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async ({ locationId, formValues, latitude, longitude }: CreateAreaParams) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      // Get template for generating area config
      const template = getSingleUnitTemplate()

      // Generate area using template
      const areaConfig = template.generateBuilding({
        wall_thickness_ft: singleUnitDefaultValues.wall_thickness_ft,
        ...formValues,
        latitude,
        longitude,
      })

      // Convert to HangarInsert format
      const hangarInsert: HangarInsert = {
        organization_id: currentOrganization.id,
        location_id: locationId,
        name: areaConfig.name,
        area_code: areaConfig.code,
        type: areaConfig.type,
        exterior_width: areaConfig.exterior_width,
        exterior_depth: areaConfig.exterior_depth,
        exterior_height: areaConfig.exterior_height,
        latitude,
        longitude,
        rotation: formValues.bearing_deg,
        door_width: areaConfig.door_width,
        door_height: areaConfig.door_height,
        status: 'active',
        type_config: areaConfig.type_config,
        settings: areaConfig.settings,
      }

      return createHangar(hangarInsert)
    },
    onSuccess: () => {
      // Invalidate ALL hangar queries (including location-specific ones)
      queryClient.invalidateQueries({ queryKey: hangarKeys.all })
    },
  })
}

/**
 * Save an area (create or update) along with its unit
 * Handles both new areas and editing existing ones
 */
export function useSaveArea() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async ({
      locationId,
      formValues,
      latitude,
      longitude,
      areaId,
      unitId,
      pricing,
    }: SaveAreaParams) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      // Get template for generating area config
      const template = getSingleUnitTemplate()

      // Generate area using template
      const areaConfig = template.generateBuilding({
        wall_thickness_ft: singleUnitDefaultValues.wall_thickness_ft,
        ...formValues,
        latitude,
        longitude,
      })

      // Generate unit config
      const unitConfigs = template.generateUnits(areaConfig, {
        wall_thickness_ft: singleUnitDefaultValues.wall_thickness_ft,
        ...formValues,
        latitude,
        longitude,
      })
      const unitConfig = unitConfigs[0] // Single unit for this template

      if (areaId) {
        // UPDATE existing area
        const hangarUpdate: HangarUpdate = {
          name: areaConfig.name,
          area_code: areaConfig.code,
          exterior_width: areaConfig.exterior_width,
          exterior_depth: areaConfig.exterior_depth,
          exterior_height: areaConfig.exterior_height,
          latitude,
          longitude,
          rotation: formValues.bearing_deg,
          door_width: areaConfig.door_width,
          door_height: areaConfig.door_height,
          type_config: areaConfig.type_config,
          settings: areaConfig.settings,
          // Pricing & features
          ...(pricing && {
            pricing_model: pricing.pricing_model,
            monthly_rate_private: pricing.monthly_rate_private ?? null,
            monthly_rate_shared: pricing.monthly_rate_shared ?? null,
            transient_rate_nightly: pricing.transient_rate_nightly ?? null,
            rate_per_sqft_private: pricing.rate_per_sqft_private ?? null,
            rate_per_sqft_shared: pricing.rate_per_sqft_shared ?? null,
            construction_year: pricing.construction_year ?? null,
            fire_suppression: pricing.fire_suppression ?? false,
            security_system: pricing.security_system ?? false,
          }),
        }

        const updatedArea = await updateHangar(areaId, hangarUpdate)

        // Update unit if it exists
        if (unitId && unitConfig) {
          await updateUnit(unitId, {
            unit_code: unitConfig.code,
            interior_width: unitConfig.interior_width,
            interior_depth: unitConfig.interior_depth,
            interior_height: unitConfig.interior_height,
            door_width: unitConfig.door_width,
            door_height: unitConfig.door_height,
            door_type: unitConfig.door_type,
          })
        }

        return updatedArea
      } else {
        // CREATE new area
        const hangarInsert: HangarInsert = {
          organization_id: currentOrganization.id,
          location_id: locationId,
          name: areaConfig.name,
          area_code: areaConfig.code,
          type: areaConfig.type,
          exterior_width: areaConfig.exterior_width,
          exterior_depth: areaConfig.exterior_depth,
          exterior_height: areaConfig.exterior_height,
          latitude,
          longitude,
          rotation: formValues.bearing_deg,
          door_width: areaConfig.door_width,
          door_height: areaConfig.door_height,
          status: 'active',
          type_config: areaConfig.type_config,
          settings: areaConfig.settings,
          // Pricing & features
          ...(pricing && {
            pricing_model: pricing.pricing_model,
            monthly_rate_private: pricing.monthly_rate_private ?? null,
            monthly_rate_shared: pricing.monthly_rate_shared ?? null,
            transient_rate_nightly: pricing.transient_rate_nightly ?? null,
            rate_per_sqft_private: pricing.rate_per_sqft_private ?? null,
            rate_per_sqft_shared: pricing.rate_per_sqft_shared ?? null,
            construction_year: pricing.construction_year ?? null,
            fire_suppression: pricing.fire_suppression ?? false,
            security_system: pricing.security_system ?? false,
          }),
        }

        const newArea = await createHangar(hangarInsert)

        // Create unit for new area
        if (unitConfig) {
          await createUnit({
            area_id: newArea.id,
            unit_code: unitConfig.code,
            name: `${areaConfig.name} Unit`,
            type: 'conventional_hangar',
            capacity_strategy: 'stackable',
            interior_width: unitConfig.interior_width,
            interior_depth: unitConfig.interior_depth,
            interior_height: unitConfig.interior_height,
            door_width: unitConfig.door_width,
            door_height: unitConfig.door_height,
            door_type: unitConfig.door_type,
            offset_x: 0,
            offset_y: 0,
            is_leasable: true,
            status: 'available',
          })
        }

        return newArea
      }
    },
    onSuccess: (_, { areaId }) => {
      // Invalidate ALL hangar queries (including location-specific ones)
      queryClient.invalidateQueries({ queryKey: hangarKeys.all })
      queryClient.invalidateQueries({ queryKey: unitKeys.all })

      if (areaId) {
        toast.success('Area updated successfully')
      } else {
        toast.success('Area created successfully')
      }
    },
    onError: (error: Error, { areaId }) => {
      const action = areaId ? 'update' : 'create'
      toast.error(`Failed to ${action} area`, {
        description: error.message,
      })
    },
  })
}

// ============================================================================
// MULTI-UNIT AREA HOOKS
// ============================================================================

interface SaveMultiUnitAreaParams {
  locationId: string
  templateType: 'single_row' | 'double_row' | 'standard_t_hangar' | 'nested_t_hangar' | 'arch_hangar'
  params: SingleRowParams | DoubleRowParams | THangarParams | NestedTHangarParams | ArchHangarParams
  areaId?: string | null
  // Optional pricing data
  pricing?: PricingFormFields
}

/**
 * Save a multi-unit area (single-row or double-row template)
 * Creates area + multiple units in a transaction
 */
export function useSaveMultiUnitArea() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async ({
      locationId,
      templateType,
      params,
      areaId,
      pricing,
    }: SaveMultiUnitAreaParams) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      // Get template based on type and generate area config
      let areaConfig
      let unitConfigs
      if (templateType === 'single_row') {
        areaConfig = getSingleRowTemplate().generateBuilding(params as SingleRowParams)
        unitConfigs = getSingleRowTemplate().generateUnits(areaConfig, params as SingleRowParams)
      } else if (templateType === 'standard_t_hangar') {
        areaConfig = getStandardTHangarTemplate().generateBuilding(params as THangarParams)
        unitConfigs = getStandardTHangarTemplate().generateUnits(areaConfig, params as THangarParams)
      } else if (templateType === 'nested_t_hangar') {
        areaConfig = getNestedTHangarTemplate().generateBuilding(params as NestedTHangarParams)
        unitConfigs = getNestedTHangarTemplate().generateUnits(areaConfig, params as NestedTHangarParams)
      } else if (templateType === 'arch_hangar') {
        areaConfig = getArchHangarTemplate().generateBuilding(params as ArchHangarParams)
        unitConfigs = getArchHangarTemplate().generateUnits(areaConfig, params as ArchHangarParams)
      } else {
        areaConfig = getDoubleRowTemplate().generateBuilding(params as DoubleRowParams)
        unitConfigs = getDoubleRowTemplate().generateUnits(areaConfig, params as DoubleRowParams)
      }

      if (areaId) {
        // UPDATE existing area
        const hangarUpdate: HangarUpdate = {
          name: areaConfig.name,
          area_code: areaConfig.code,
          exterior_width: areaConfig.exterior_width,
          exterior_depth: areaConfig.exterior_depth,
          exterior_height: areaConfig.exterior_height,
          latitude: params.latitude,
          longitude: params.longitude,
          rotation: params.bearing_deg,
          door_width: areaConfig.door_width,
          door_height: areaConfig.door_height,
          type_config: areaConfig.type_config,
          settings: areaConfig.settings,
          // Pricing & features
          ...(pricing && {
            pricing_model: pricing.pricing_model,
            monthly_rate_private: pricing.monthly_rate_private ?? null,
            monthly_rate_shared: pricing.monthly_rate_shared ?? null,
            transient_rate_nightly: pricing.transient_rate_nightly ?? null,
            rate_per_sqft_private: pricing.rate_per_sqft_private ?? null,
            rate_per_sqft_shared: pricing.rate_per_sqft_shared ?? null,
            construction_year: pricing.construction_year ?? null,
            fire_suppression: pricing.fire_suppression ?? false,
            security_system: pricing.security_system ?? false,
          }),
        }

        const updatedArea = await updateHangar(areaId, hangarUpdate)

        // Delete existing units and recreate (simpler than diffing)
        await deleteHangarUnits(areaId)

        // Create new units
        for (const unitConfig of unitConfigs) {
          await createUnit({
            area_id: areaId,
            unit_code: unitConfig.code,
            name: `${areaConfig.name} ${unitConfig.code}`,
            type: unitConfig.type,
            capacity_strategy: unitConfig.capacity_strategy,
            interior_width: unitConfig.interior_width,
            interior_depth: unitConfig.interior_depth,
            interior_height: unitConfig.interior_height,
            door_width: unitConfig.door_width,
            door_height: unitConfig.door_height,
            door_type: unitConfig.door_type || ('default_door_type' in params ? params.default_door_type : 'bifold'),
            offset_x: unitConfig.offset_x,
            offset_y: unitConfig.offset_y,
            unit_rotation: unitConfig.unit_rotation,  // Critical for double-row (Row B rotated 180°)
            is_leasable: true,
            status: 'available',
          })
        }

        return updatedArea
      } else {
        // CREATE new area
        const hangarInsert: HangarInsert = {
          organization_id: currentOrganization.id,
          location_id: locationId,
          name: areaConfig.name,
          area_code: areaConfig.code,
          type: areaConfig.type,
          exterior_width: areaConfig.exterior_width,
          exterior_depth: areaConfig.exterior_depth,
          exterior_height: areaConfig.exterior_height,
          latitude: params.latitude,
          longitude: params.longitude,
          rotation: params.bearing_deg,
          door_width: areaConfig.door_width,
          door_height: areaConfig.door_height,
          status: 'active',
          type_config: areaConfig.type_config,
          settings: areaConfig.settings,
          // Pricing & features
          ...(pricing && {
            pricing_model: pricing.pricing_model,
            monthly_rate_private: pricing.monthly_rate_private ?? null,
            monthly_rate_shared: pricing.monthly_rate_shared ?? null,
            transient_rate_nightly: pricing.transient_rate_nightly ?? null,
            rate_per_sqft_private: pricing.rate_per_sqft_private ?? null,
            rate_per_sqft_shared: pricing.rate_per_sqft_shared ?? null,
            construction_year: pricing.construction_year ?? null,
            fire_suppression: pricing.fire_suppression ?? false,
            security_system: pricing.security_system ?? false,
          }),
        }

        const newArea = await createHangar(hangarInsert)

        // Create all units
        for (const unitConfig of unitConfigs) {
          await createUnit({
            area_id: newArea.id,
            unit_code: unitConfig.code,
            name: `${areaConfig.name} ${unitConfig.code}`,
            type: unitConfig.type,
            capacity_strategy: unitConfig.capacity_strategy,
            interior_width: unitConfig.interior_width,
            interior_depth: unitConfig.interior_depth,
            interior_height: unitConfig.interior_height,
            door_width: unitConfig.door_width,
            door_height: unitConfig.door_height,
            door_type: unitConfig.door_type || ('default_door_type' in params ? params.default_door_type : 'bifold'),
            offset_x: unitConfig.offset_x,
            offset_y: unitConfig.offset_y,
            unit_rotation: unitConfig.unit_rotation,  // Critical for double-row (Row B rotated 180°)
            is_leasable: true,
            status: 'available',
          })
        }

        return newArea
      }
    },
    onSuccess: (_, { areaId, templateType, params }) => {
      // Invalidate ALL hangar queries (including location-specific ones)
      queryClient.invalidateQueries({ queryKey: hangarKeys.all })
      queryClient.invalidateQueries({ queryKey: unitKeys.all })

      // Calculate unit count based on template type
      let unitCount: number
      if (templateType === 'single_row') {
        unitCount = (params as SingleRowParams).unit_count
      } else if (templateType === 'standard_t_hangar') {
        unitCount = (params as THangarParams).unit_count
      } else if (templateType === 'nested_t_hangar') {
        const np = params as NestedTHangarParams
        unitCount = 2 * np.unit_count - 1
      } else if (templateType === 'arch_hangar') {
        unitCount = 1
      } else {
        unitCount = (params as DoubleRowParams).row_a.unit_count + (params as DoubleRowParams).row_b.unit_count
      }

      if (areaId) {
        toast.success(`Area updated with ${unitCount} units`)
      } else {
        toast.success(`Area created with ${unitCount} units`)
      }
    },
    onError: (error: Error, { areaId }) => {
      const action = areaId ? 'update' : 'create'
      toast.error(`Failed to ${action} multi-unit area`, {
        description: error.message,
      })
    },
  })
}

// ============================================================================
// OUTDOOR AREA HOOKS (Ramp / Tiedown)
// ============================================================================

interface SaveOutdoorAreaParams {
  locationId: string
  formValues: OutdoorRectangularFormValues
  latitude: number
  longitude: number
  areaId?: string | null
  unitId?: string | null
  pricing?: PricingFormFields
  /** Event auto-placement settings authored on the area (Auto-Placement tab). */
  placement?: AreaPlacementFieldsInput
}

/**
 * Save an outdoor area (ramp or tiedown) — create or update
 */
export function useSaveOutdoorArea() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async ({
      locationId,
      formValues,
      latitude,
      longitude,
      areaId,
      unitId,
      pricing,
      placement,
    }: SaveOutdoorAreaParams) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const template = getOutdoorRectangularTemplate()

      // Generate area config using template
      const areaConfig = template.generateBuilding({
        ...formValues,
        wall_color: '#A0A0A0',
        latitude,
        longitude,
      })

      // Generate unit config (single unit for outdoor areas)
      const unitConfigs = template.generateUnits(areaConfig, {
        ...formValues,
        wall_color: '#A0A0A0',
        latitude,
        longitude,
      })
      const unitConfig = unitConfigs[0]

      if (areaId) {
        // UPDATE existing area
        const hangarUpdate: HangarUpdate = {
          name: areaConfig.name,
          area_code: areaConfig.code,
          exterior_width: areaConfig.exterior_width,
          exterior_depth: areaConfig.exterior_depth,
          exterior_height: 0,
          latitude,
          longitude,
          rotation: formValues.bearing_deg,
          door_width: 0,
          door_height: 0,
          type_config: areaConfig.type_config,
          settings: areaConfig.settings,
          ...(pricing && {
            pricing_model: pricing.pricing_model,
            monthly_rate_private: pricing.monthly_rate_private ?? null,
            monthly_rate_shared: pricing.monthly_rate_shared ?? null,
            transient_rate_nightly: pricing.transient_rate_nightly ?? null,
            rate_per_sqft_private: pricing.rate_per_sqft_private ?? null,
            rate_per_sqft_shared: pricing.rate_per_sqft_shared ?? null,
            construction_year: pricing.construction_year ?? null,
            fire_suppression: pricing.fire_suppression ?? false,
            security_system: pricing.security_system ?? false,
          }),
          // Event auto-placement settings (area level)
          ...(placement ?? {}),
        }

        const updatedArea = await updateHangar(areaId, hangarUpdate)

        // Update unit if it exists
        if (unitId && unitConfig) {
          await updateUnit(unitId, {
            unit_code: unitConfig.code,
            interior_width: unitConfig.interior_width,
            interior_depth: unitConfig.interior_depth,
            interior_height: 0,
            door_width: null,
            door_height: null,
            door_type: null,
          })
        }

        return updatedArea
      } else {
        // CREATE new area
        const hangarInsert: HangarInsert = {
          organization_id: currentOrganization.id,
          location_id: locationId,
          name: areaConfig.name,
          area_code: areaConfig.code,
          type: areaConfig.type,
          exterior_width: areaConfig.exterior_width,
          exterior_depth: areaConfig.exterior_depth,
          exterior_height: 0,
          latitude,
          longitude,
          rotation: formValues.bearing_deg,
          door_width: 0,
          door_height: 0,
          status: 'active',
          type_config: areaConfig.type_config,
          settings: areaConfig.settings,
          ...(pricing && {
            pricing_model: pricing.pricing_model,
            monthly_rate_private: pricing.monthly_rate_private ?? null,
            monthly_rate_shared: pricing.monthly_rate_shared ?? null,
            transient_rate_nightly: pricing.transient_rate_nightly ?? null,
            rate_per_sqft_private: pricing.rate_per_sqft_private ?? null,
            rate_per_sqft_shared: pricing.rate_per_sqft_shared ?? null,
            construction_year: pricing.construction_year ?? null,
            fire_suppression: pricing.fire_suppression ?? false,
            security_system: pricing.security_system ?? false,
          }),
          // Event auto-placement settings (area level)
          ...(placement ?? {}),
        }

        const newArea = await createHangar(hangarInsert)

        // Create unit for new area
        if (unitConfig) {
          await createUnit({
            area_id: newArea.id,
            unit_code: unitConfig.code,
            name: `${areaConfig.name} Unit`,
            type: unitConfig.type,
            capacity_strategy: unitConfig.capacity_strategy,
            interior_width: unitConfig.interior_width,
            interior_depth: unitConfig.interior_depth,
            interior_height: 0,
            door_width: null,
            door_height: null,
            door_type: null,
            offset_x: 0,
            offset_y: 0,
            is_leasable: true,
            status: 'available',
          })
        }

        return newArea
      }
    },
    onSuccess: (_, { areaId }) => {
      queryClient.invalidateQueries({ queryKey: hangarKeys.all })
      queryClient.invalidateQueries({ queryKey: unitKeys.all })

      if (areaId) {
        toast.success('Outdoor area updated successfully')
      } else {
        toast.success('Outdoor area created successfully')
      }
    },
    onError: (error: Error, { areaId }) => {
      const action = areaId ? 'update' : 'create'
      toast.error(`Failed to ${action} outdoor area`, {
        description: error.message,
      })
    },
  })
}

// ============================================================================
// EXIT ZONE HOOK ("Runway Exit N" — non-allocatable placement infrastructure)
// ============================================================================

/** Footprint (feet) of the small square marker that represents an exit area. */
const EXIT_MARKER_SIZE_FT = 20

interface SaveExitZoneParams {
  locationId: string
  name: string
  /** Area code; caller may pass a pre-generated one (defaults to an EXT- code). */
  areaCode?: string
  latitude: number
  longitude: number
  /** Approach bearing onto the taxiway (degrees). Also orients the marker. */
  exitHeading: number
  /** Aircraft per departure slot this exit can push (optional). */
  exitThroughput?: number | null
  areaId?: string | null
}

/**
 * Save an Exit Zone (create or update). An exit area is a normal `areas` row
 * with type `exit_point` / area_category `exit`, a tiny marker footprint, and
 * NO generated units (non-allocatable). It carries the exit vector
 * (center = lat/lng, heading = exit_heading) the placer/routing read.
 */
export function useSaveExitZone() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async ({
      locationId,
      name,
      areaCode,
      latitude,
      longitude,
      exitHeading,
      exitThroughput,
      areaId,
    }: SaveExitZoneParams) => {
      if (!currentOrganization?.id) {
        throw new Error('No organization selected')
      }

      const code =
        areaCode ?? `${AREA_TYPE_PREFIXES.exit_point}-${name.replace(/\s+/g, '-').toUpperCase()}`

      if (areaId) {
        const hangarUpdate: HangarUpdate = {
          name,
          area_code: code,
          latitude,
          longitude,
          rotation: exitHeading,
          area_category: 'exit',
          exit_heading: exitHeading,
          exit_throughput: exitThroughput ?? null,
        }
        return updateHangar(areaId, hangarUpdate)
      }

      const hangarInsert: HangarInsert = {
        organization_id: currentOrganization.id,
        location_id: locationId,
        name,
        area_code: code,
        type: 'exit_point',
        exterior_width: EXIT_MARKER_SIZE_FT,
        exterior_depth: EXIT_MARKER_SIZE_FT,
        exterior_height: 0,
        latitude,
        longitude,
        rotation: exitHeading,
        door_width: 0,
        door_height: 0,
        status: 'active',
        // Event placement (exit area — non-allocatable, no units)
        area_category: 'exit',
        exit_heading: exitHeading,
        exit_throughput: exitThroughput ?? null,
      }

      // Exit areas intentionally create NO units.
      return createHangar(hangarInsert)
    },
    onSuccess: (_, { areaId }) => {
      queryClient.invalidateQueries({ queryKey: hangarKeys.all })

      if (areaId) {
        toast.success('Exit zone updated successfully')
      } else {
        toast.success('Exit zone created successfully')
      }
    },
    onError: (error: Error, { areaId }) => {
      const action = areaId ? 'update' : 'create'
      toast.error(`Failed to ${action} exit zone`, {
        description: error.message,
      })
    },
  })
}
